import { useQuery } from '@tanstack/react-query';
import { format, isValid } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import { getFullyBookedClassesAPI } from '~entities/classes/api/get-fully-booked-classes';
import { reportingActiveStudentsClassCountAPI } from '~entities/payment/api/reporting-active-students-class-count';
import { reportingClassGenreReportAPI } from '~entities/payment/api/reporting-class-genre-report';
import { reportingDaysPastDueOverduePaymentsAPI } from '~entities/payment/api/reporting-days-past-due-overdue-payments';
import { reportingEnrollsByClassAPI } from '~entities/payment/api/reporting-enrolls-by-class';
import { reportingNewVsReturningStudentsAPI } from '~entities/payment/api/reporting-new-vs-returning-students';
import { reportingGroupEnrollmentsAPI } from '~entities/payment/api/reporting-group-enrollments';
import { reportingSessionStudentsEnrollmentsAPI } from '~entities/payment/api/reporting-session-students-enrollments';
import { reportingFailedPaymentsAPI } from '~entities/payment/api/reporting-failed-payments';
import { reportingIssuedRefundsAPI } from '~entities/payment/api/reporting-issued-refunds';
import { reportingOutstandingAndOverdueAPI } from '~entities/payment/api/reporting-outstanding-and-overdue';
import { reportingParentOutstandingBalancesAPI } from '~entities/payment/api/reporting-parent-outstanding-balances';
import { reportingPaymentsByClassAPI } from '~entities/payment/api/reporting-payments-by-class';
import { reportingPaymentsByDueDateAPI } from '~entities/payment/api/reporting-payments-by-due-date';
import { reportingPaymentsBySessionAPI } from '~entities/payment/api/reporting-payments-by-session';
import { reportingRevenueByLocationAPI } from '~entities/payment/api/reporting-revenue-by-location';
import { reportingRevenueOverTimePeriodAPI } from '~entities/payment/api/reporting-revenue-over-time-period';
import { reportingScheduledUpcomingPaymentsAPI } from '~entities/payment/api/reporting-scheduled-upcoming-payments';
import { reportingDroppedClassReportAPI } from '~entities/payment/api/reporting-dropped-class-report';
import { reportingEnrollmentBySessionAPI } from '~entities/payment/api/reporting-session-enrollment-report';
import { reportingSessionPaymentsDetailsAPI } from '~entities/payment/api/reporting-session-payments-details';
import { reportingTopSpendingParentsAPI } from '~entities/payment/api/reporting-top-spending-parents';
import {
  formatDateParam,
  reportingTransactionDetailsAPI,
} from '~entities/payment/api/reporting-transaction-details';
import { paymentProviderStatementAPI } from '~entities/payment/api/reporting/payment-provider-statement';
import { transactionCodesReportAPI } from '~entities/payment/api/reporting/transaction-codes-report';
import type { PaymentStatus } from '~entities/payment/constants';
import {
  reportingBirthdaysAPI,
  type BirthdayStatusFilter,
} from '~entities/student/api/reporting-birthdays';
import { reportingTrialClassReportAPI } from '~entities/student/api/reporting-trial-class-report';

import {
  ALL_MONTHS,
  type MonthSelectorValue,
  ReportType,
} from '~pages/payments/payment-reports/report-config';

import { KEYS } from '~shared/api/query-keys';

interface UseReportQueriesParams {
  studioId: string;
  dateRanges: Record<string, DateRange | undefined>;
  selectedSession: { label: string; value: string } | null;
  selectedClass: { label: string; value: string } | null;
  selectedGroup: { label: string; value: string } | null;
  selectedLocation: { label: string | null; value: string | null } | null;
  daysPastDue: number | '';
  stripePaymentIntents: number | string;
  transactionCodesLookbackDays: number | string;
  dueDate: Date | undefined;
  paymentStatus: PaymentStatus | undefined;
  selectedMonth: MonthSelectorValue;
  birthdayStatus: BirthdayStatusFilter;
  classKeywords: string;
}

export function useReportQueries({
  studioId,
  dateRanges,
  selectedSession,
  selectedClass,
  selectedGroup,
  selectedLocation,
  daysPastDue,
  stripePaymentIntents,
  transactionCodesLookbackDays,
  dueDate,
  paymentStatus,
  selectedMonth,
  birthdayStatus,
  classKeywords,
}: UseReportQueriesParams) {
  const enrollmentBySessionQuery = useQuery({
    queryKey: [
      KEYS.SESSION_ENROLLMENT_REPORT,
      selectedSession?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedSession
        ? reportingEnrollmentBySessionAPI({
            sessionId: selectedSession.value,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const sessionPaymentsDetailsQuery = useQuery({
    queryKey: [
      KEYS.SESSION_PAYMENTS_DETAILS,
      selectedSession?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedSession
        ? reportingSessionPaymentsDetailsAPI({
            sessionId: selectedSession.value,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const parentOutstandingBalancesQuery = useQuery({
    queryKey: [
      KEYS.PARENT_OUTSTANDING_BALANCES,
      undefined,
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      reportingParentOutstandingBalancesAPI({ format: KEYS.EXPORT_FORMAT.CSV }),
    enabled: false,
  });

  const outstandingAndOverdueQuery = useQuery({
    queryKey: [KEYS.OUTSTANDING_AND_OVERDUE, KEYS.EXPORT_FORMAT.CSV],
    queryFn: () =>
      reportingOutstandingAndOverdueAPI({ format: KEYS.EXPORT_FORMAT.CSV }),
    enabled: false,
  });

  const paymentsBySessionQuery = useQuery({
    queryKey: [
      KEYS.PAYMENTS_BY_SESSION,
      selectedSession?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedSession
        ? reportingPaymentsBySessionAPI({
            sessionId: selectedSession.value,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const paymentsByClassQuery = useQuery({
    queryKey: [
      KEYS.PAYMENTS_BY_CLASS,
      selectedClass?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedClass
        ? reportingPaymentsByClassAPI({
            classId: selectedClass.value,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const revenueOverTimePeriodQuery = useQuery({
    queryKey: [
      KEYS.REVENUE_OVER_TIME_PERIOD,
      formatDateParam(dateRanges[ReportType.RevenueOverPeriod]?.from ?? ''),
      formatDateParam(dateRanges[ReportType.RevenueOverPeriod]?.to ?? ''),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () => {
      const range = dateRanges[ReportType.RevenueOverPeriod];
      if (!range?.from || !range.to) return Promise.resolve('');
      return reportingRevenueOverTimePeriodAPI({
        startDate: isValid(range.from) ? formatDateParam(range.from) : '',
        endDate: isValid(range.to) ? formatDateParam(range.to) : '',
        format: KEYS.EXPORT_FORMAT.CSV,
      });
    },
    enabled: false,
  });

  const topSpendingParentsQuery = useQuery({
    queryKey: [KEYS.TOP_SPENDING_PARENTS],
    queryFn: () =>
      reportingTopSpendingParentsAPI({ format: KEYS.EXPORT_FORMAT.CSV }),
    enabled: false,
  });

  const scheduledUpcomingPaymentsQuery = useQuery({
    queryKey: [
      KEYS.SCHEDULED_UPCOMING_PAYMENTS,
      dateRanges[ReportType.ScheduledUpcomingPayments]?.from?.toISOString(),
      dateRanges[ReportType.ScheduledUpcomingPayments]?.to?.toISOString(),
    ],
    queryFn: () =>
      dateRanges[ReportType.ScheduledUpcomingPayments]?.from &&
      dateRanges[ReportType.ScheduledUpcomingPayments].to
        ? reportingScheduledUpcomingPaymentsAPI({
            startDate: isValid(
              dateRanges[ReportType.ScheduledUpcomingPayments].from,
            )
              ? dateRanges[
                  ReportType.ScheduledUpcomingPayments
                ].from.toISOString()
              : '',
            endDate: isValid(
              dateRanges[ReportType.ScheduledUpcomingPayments].to,
            )
              ? dateRanges[
                  ReportType.ScheduledUpcomingPayments
                ].to.toISOString()
              : '',
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const issuedRefundsQuery = useQuery({
    queryKey: [
      KEYS.ISSUED_REFUND,
      dateRanges[ReportType.IssuedRefunds]?.from?.toISOString(),
      dateRanges[ReportType.IssuedRefunds]?.to?.toISOString(),
    ],
    queryFn: () =>
      dateRanges[ReportType.IssuedRefunds]?.from &&
      dateRanges[ReportType.IssuedRefunds].to
        ? reportingIssuedRefundsAPI({
            startDate: isValid(dateRanges[ReportType.IssuedRefunds].from)
              ? dateRanges[ReportType.IssuedRefunds].from.toISOString()
              : '',
            endDate: isValid(dateRanges[ReportType.IssuedRefunds].to)
              ? dateRanges[ReportType.IssuedRefunds].to.toISOString()
              : '',
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const failedPaymentsQuery = useQuery({
    queryKey: [
      KEYS.FAILED_PAYMENTS,
      dateRanges[ReportType.FailedPayments]?.from?.toISOString(),
      dateRanges[ReportType.FailedPayments]?.to?.toISOString(),
    ],
    queryFn: () =>
      dateRanges[ReportType.FailedPayments]?.from &&
      dateRanges[ReportType.FailedPayments].to
        ? reportingFailedPaymentsAPI({
            startDate: isValid(dateRanges[ReportType.FailedPayments].from)
              ? dateRanges[ReportType.FailedPayments].from.toISOString()
              : '',
            endDate: isValid(dateRanges[ReportType.FailedPayments].to)
              ? dateRanges[ReportType.FailedPayments].to.toISOString()
              : '',
            format: KEYS.EXPORT_FORMAT.CSV,
          }).then((res) => res as string)
        : Promise.resolve(''),
    enabled: false,
  });

  const daysPastDueOverduePaymentsQuery = useQuery({
    queryKey: [KEYS.DAYS_PAST_DUE_OVERDUE_PAYMENTS, daysPastDue],
    queryFn: () =>
      typeof daysPastDue === 'number' && daysPastDue > 0
        ? reportingDaysPastDueOverduePaymentsAPI({
            dueDays: daysPastDue,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const activeStudentsClassCountQuery = useQuery({
    queryKey: [KEYS.ACTIVE_STUDENTS_CLASS_COUNT_REPORT],
    queryFn: () =>
      reportingActiveStudentsClassCountAPI({
        format: KEYS.EXPORT_FORMAT.CSV,
        locationId: studioId,
      }),
    enabled: false,
  });

  const enrollsByClassQuery = useQuery({
    queryKey: [KEYS.CLASS_ENROLLMENTS_WITH_STUDENT_DETAILS_REPORT],
    queryFn: () =>
      reportingEnrollsByClassAPI({
        format: KEYS.EXPORT_FORMAT.CSV,
        startDate:
          dateRanges[
            ReportType.ClassEnrollmentsWithStudentDetails
          ]?.from?.toISOString() ?? '',
        endDate:
          dateRanges[
            ReportType.ClassEnrollmentsWithStudentDetails
          ]?.to?.toISOString() ?? '',
      }),
    enabled: false,
  });

  const newVsReturningStudentsQuery = useQuery({
    queryKey: [
      KEYS.NEW_VS_RETURNING_STUDENTS_REPORT,
      selectedSession?.value ?? '',
      dateRanges[ReportType.NewVsReturningStudents]?.from?.toISOString() ?? '',
      dateRanges[ReportType.NewVsReturningStudents]?.to?.toISOString() ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      reportingNewVsReturningStudentsAPI({
        sessionId: selectedSession?.value ?? undefined,
        startDate:
          dateRanges[ReportType.NewVsReturningStudents]?.from?.toISOString() ??
          undefined,
        endDate:
          dateRanges[ReportType.NewVsReturningStudents]?.to?.toISOString() ??
          undefined,
        format: KEYS.EXPORT_FORMAT.CSV,
      }),
    enabled: false,
  });

  const enrollsByClassAndEventQuery = useQuery({
    queryKey: [KEYS.CLASS_AND_EVENT_ENROLLMENTS_REPORT],
    queryFn: () =>
      reportingEnrollsByClassAPI({
        format: KEYS.EXPORT_FORMAT.CSV,
        includeEvents: true,
        startDate:
          dateRanges[ReportType.ClassAndEventEnrollments]?.from?.toISOString() ??
          '',
        endDate:
          dateRanges[ReportType.ClassAndEventEnrollments]?.to?.toISOString() ??
          '',
      }),
    enabled: false,
  });

  const transactionDetailsQuery = useQuery({
    queryKey: [
      KEYS.TRANSACTION_DETAILS_REPORT,
      formatDateParam(dateRanges[ReportType.TransactionDetails]?.from ?? ''),
      formatDateParam(dateRanges[ReportType.TransactionDetails]?.to ?? ''),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      reportingTransactionDetailsAPI({
        format: KEYS.EXPORT_FORMAT.CSV,
        startDate: dateRanges[ReportType.TransactionDetails]?.from ?? '',
        endDate: dateRanges[ReportType.TransactionDetails]?.to ?? '',
      }),
    enabled: false,
  });

  const paymentProviderStatementQuery = useQuery({
    queryKey: [
      KEYS.PAYMENT_PROVIDER_STATEMENT,
      Number(stripePaymentIntents),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      paymentProviderStatementAPI({
        totalDays: Number(stripePaymentIntents),
        format: KEYS.EXPORT_FORMAT.CSV,
      }),
    enabled: false,
  });

  const transactionCodesReportQuery = useQuery({
    queryKey: [
      KEYS.TRANSACTION_CODES_REPORT,
      Number(transactionCodesLookbackDays),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      transactionCodesReportAPI({
        totalDays: Number(transactionCodesLookbackDays),
        format: KEYS.EXPORT_FORMAT.CSV,
      }),
    enabled: false,
  });

  const paymentsByDueDateQuery = useQuery({
    queryKey: [
      KEYS.PAYMENTS_BY_DUE_DATE,
      format(dueDate ?? new Date(), 'yyyy-MM-dd'),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      dueDate
        ? reportingPaymentsByDueDateAPI({
            dueDate: format(dueDate, 'yyyy-MM-dd'),
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const revenueByLocationQuery = useQuery({
    queryKey: [
      KEYS.REVENUE_BY_LOCATION,
      selectedLocation?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
      formatDateParam(dateRanges[ReportType.RevenueByLocation]?.from ?? ''),
      formatDateParam(dateRanges[ReportType.RevenueByLocation]?.to ?? ''),
      paymentStatus,
    ],
    queryFn: () => {
      const range = dateRanges[ReportType.RevenueByLocation];
      if (!selectedLocation?.value || !range?.from || !range.to) {
        return Promise.resolve('');
      }
      return reportingRevenueByLocationAPI({
        startDate: isValid(range.from) ? range.from.toISOString() : '',
        endDate: isValid(range.to) ? range.to.toISOString() : '',
        locationId: selectedLocation.value,
        status: paymentStatus,
        format: KEYS.EXPORT_FORMAT.CSV,
      });
    },
    enabled: false,
  });

  const trialClassReportQuery = useQuery({
    queryKey: [
      KEYS.TRIAL_CLASS_REPORT,
      dateRanges[ReportType.TrialClassReport]?.from?.toISOString(),
      dateRanges[ReportType.TrialClassReport]?.to?.toISOString(),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      dateRanges[ReportType.TrialClassReport]?.from &&
      dateRanges[ReportType.TrialClassReport].to
        ? reportingTrialClassReportAPI({
            startDate:
              dateRanges[ReportType.TrialClassReport].from.toISOString(),
            endDate: dateRanges[ReportType.TrialClassReport].to.toISOString(),
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const droppedClassReportQuery = useQuery({
    queryKey: [
      KEYS.DROPPED_CLASS_REPORT,
      formatDateParam(dateRanges[ReportType.DroppedClassReport]?.from ?? ''),
      formatDateParam(dateRanges[ReportType.DroppedClassReport]?.to ?? ''),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () => {
      const range = dateRanges[ReportType.DroppedClassReport];
      if (!range?.from || !range.to) {
        return Promise.resolve('');
      }
      return reportingDroppedClassReportAPI({
        startDate: isValid(range.from) ? formatDateParam(range.from) : '',
        endDate: isValid(range.to) ? formatDateParam(range.to) : '',
        format: KEYS.EXPORT_FORMAT.CSV,
      });
    },
    enabled: false,
  });

  const birthdayReportQuery = useQuery({
    queryKey: [
      KEYS.BIRTHDAYS_REPORT,
      selectedMonth,
      birthdayStatus,
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      reportingBirthdaysAPI({
        month:
          selectedMonth === ALL_MONTHS ? undefined : parseInt(selectedMonth),
        status: birthdayStatus,
        format: KEYS.EXPORT_FORMAT.CSV,
      }),
    enabled: false,
  });

  const fullyBookedClassesBySessionQuery = useQuery({
    queryKey: [
      KEYS.FULLY_BOOKED_CLASSES,
      selectedSession?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedSession?.value
        ? getFullyBookedClassesAPI({
            sessionId: selectedSession.value,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const groupEnrollmentsReportQuery = useQuery({
    queryKey: [
      KEYS.CLASS_GROUPS_REPORT,
      selectedSession?.value ?? '',
      selectedGroup?.value ?? '',
      selectedClass?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedSession?.value && selectedGroup?.value
        ? reportingGroupEnrollmentsAPI({
            sessionId: selectedSession.value,
            groupId: selectedGroup.value,
            classId: selectedClass?.value,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const sessionStudentsEnrollmentsQuery = useQuery({
    queryKey: [
      KEYS.SESSION_STUDENTS_ENROLLMENTS_REPORT,
      selectedSession?.value ?? '',
      classKeywords.trim(),
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      selectedSession?.value
        ? reportingSessionStudentsEnrollmentsAPI({
            sessionId: selectedSession.value,
            classKeywords: classKeywords.trim() || undefined,
            format: KEYS.EXPORT_FORMAT.CSV,
          })
        : Promise.resolve(''),
    enabled: false,
  });

  const classGenreReportQuery = useQuery({
    queryKey: [
      KEYS.CLASS_GENRE_REPORT,
      selectedSession?.value ?? '',
      KEYS.EXPORT_FORMAT.CSV,
    ],
    queryFn: () =>
      reportingClassGenreReportAPI({
        sessionId: selectedSession?.value,
        format: KEYS.EXPORT_FORMAT.CSV,
      }),
    enabled: false,
  });

  return {
    enrollmentBySessionQuery,
    sessionPaymentsDetailsQuery,
    parentOutstandingBalancesQuery,
    outstandingAndOverdueQuery,
    paymentsBySessionQuery,
    paymentsByClassQuery,
    revenueOverTimePeriodQuery,
    topSpendingParentsQuery,
    scheduledUpcomingPaymentsQuery,
    issuedRefundsQuery,
    failedPaymentsQuery,
    daysPastDueOverduePaymentsQuery,
    activeStudentsClassCountQuery,
    enrollsByClassQuery,
    newVsReturningStudentsQuery,
    enrollsByClassAndEventQuery,
    transactionDetailsQuery,
    paymentProviderStatementQuery,
    transactionCodesReportQuery,
    paymentsByDueDateQuery,
    revenueByLocationQuery,
    trialClassReportQuery,
    droppedClassReportQuery,
    birthdayReportQuery,
    fullyBookedClassesBySessionQuery,
    groupEnrollmentsReportQuery,
    classGenreReportQuery,
    sessionStudentsEnrollmentsQuery,
  };
}
